import React, { useEffect, useRef } from 'react';
import { useSuppressFloatingCta } from '../floatingCtaContext';

/**
 * ONE pinned stage, TWO framings of ONE photograph.
 *
 * This is not two sections crossfading. The scene is a tall <section> whose
 * inner viewport is sticky, holding a single <img>. Scroll progress (0 -> 1)
 * drives one continuous camera travel — pan plus a mid-travel push — over that
 * photograph while the opening statement exits and the product frame arrives.
 * At progress 0 the stage IS the statement frame; at progress 1 it IS the
 * product frame.
 *
 * Custom properties are written on the scene element rather than :root, because
 * the page runs more than one scene and two scenes writing to :root would fight
 * over the same variables.
 */

/**
 * The single plate every scene on the page is framed out of. One photograph,
 * many framings: reusing it is the point, which is why scenes differ by their
 * camera slice rather than by a second image.
 */
export const SCENE_PLATE = 'https://cdn.magicpatterns.com/uploads/1HVbzDooPTq7XXJmGtkdKW/hf_20260903_101435_2ef48d0b-eac3-4f8a-8212-95988675d3da.mp4';

/**
 * The portrait cut of the same plate, served to phones. A narrow viewport takes
 * a much taller slice of the frame, so the landscape-ish master had to be blown
 * up to have anywhere to pan — this one is already the right shape, which keeps
 * it sharp and keeps the launch column on the headline's axis.
 */
export const SCENE_PLATE_MOBILE = 'https://cdn.magicpatterns.com/uploads/w9wCUjYX8ow3LYEv4fmWM6/9x16.mp4';

/**
 * Picks the plate cut for the current viewport. Watched rather than read once,
 * so a rotation or a resized preview swaps to the right cut instead of keeping
 * whichever one happened to be current at mount.
 */
function usePlateSource(desktop: string, mobile?: string) {
  const [isNarrow, setIsNarrow] = React.useState(false);
  React.useEffect(() => {
    if (!mobile) return;
    const mq = window.matchMedia('(max-width: 767px)');
    const sync = () => setIsNarrow(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, [mobile]);
  const usingMobile = isNarrow && !!mobile;
  return {
    src: usingMobile ? mobile as string : desktop,
    usingMobile
  };
}

/*
 * NO POSTER IMAGE.
 *
 * A poster only helps if it is the clip's own first frame. Anything else is a
 * DIFFERENT COMPOSITION swapping out for the real one the instant it decodes —
 * a visible cut on the first thing the reader sees, which is worse than the
 * brief void it was covering. The stills used here were near-misses rather
 * than frames, so both are gone.
 *
 * What paints the first frame instead is the PRIME below: the element plays
 * for one frame and pauses, which decodes and paints the clip's own opening
 * image on every platform. That is the real first frame, so there is nothing
 * to swap.
 *
 * THE ELEMENT IS NEVER UNMOUNTED. An earlier version swapped the video out
 * for a still after a 4s timeout if it had not decoded — which on desktop,
 * where there is no still, removed the plate entirely and left the flat void
 * colour showing forever. A slow connection is not a failure: the element
 * stays, and the prime retries.
 */

const clamp01 = (v: number) => v < 0 ? 0 : v > 1 ? 1 : v;
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
const easeOutQuart = (t: number) => 1 - Math.pow(1 - t, 4);
const easeInOutCubic = (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
const track = (p: number, from: number, to: number, ease: (t: number) => number = t => t) => ease(clamp01((p - from) / (to - from)));

// Camera endpoints, read off the plate rather than invented. The ascent is the
// plate's own subject, so the move follows the rocket: launch, then disc, then
// open sky. Each beat is composed against the region it lands on.
const CAMERA = {
  // The plate's launch column sits at 47% of its width, not 50%, so centring
  // the image leaves the rocket off-axis from the headline above it. Framing on
  // the column instead puts the ascent directly under the statement.
  fromX: -48,
  // Opens on the ascent, not the ground: pad and mountains in the lower third,
  // the plume climbing through the headline, cloud bank across the top.
  fromY: -83,
  toX: -48,
  // Stops with the disc filling the frame behind the arriving block, rather
  // than climbing past it into empty sky. The climb ends where the subject is.
  toY: -56,
  // Lands before the end and HOLDS. The last stretch of scroll is the reader
  // finishing the sentence against a settled frame, not more camera move.
  until: 0.86,
  // Push-in that PEAKS MID-TRAVEL and resolves to 1. A push that only ever
  // grows ends the scene mid-gesture; a breath resolves.
  push: 0.11
};
export type CameraFraming = Partial<typeof CAMERA>;

// Scroll binding. A straight mapping of scrollY onto the timeline collapses on
// a flick — one wheel gesture can jump the whole scene inside a single frame,
// which reads as no animation at all. Chasing the target with an exponential
// approach smooths that out. Both terms are frame-rate independent: identical
// at 60Hz and 120Hz.
//
// The rate ceiling is deliberately LOOSE. It used to hold a flick to ~2.5s,
// which was fine for a still plate but wrong for a motion one: the clip's own
// footage is scrubbed off this same value, so a hard ceiling made the video
// trail the reader's gesture by a second or more and the scene read as laggy.
// Damped-but-tracking beats slow-and-cinematic when the plate is moving.
const SETTLE = 17;
const MAX_RATE = 2.4;
const EPSILON = 0.0004;
export function CameraScene({
  photo,
  photoMobile,
  statement,
  productHeader,
  product,
  detail,
  id,
  camera,
  travel = '260svh',
  offsetTop = '0px',
  claimsBottomEdge = false,
  className = ''
}: {
  /** The single photograph the camera moves over. */
  photo: string;
  /** Portrait cut of the same plate, used below 768px. */
  photoMobile?: string;
  /** Opening framing: eyebrow, headline, lead. Wrap each block in SceneBlock. */
  statement: React.ReactNode;
  /** Pill + heading. Travels further and on a different curve to the window. */
  productHeader: React.ReactNode;
  /**
   * The product window itself. Optional: a scene can resolve on copy alone
   * when the screen it would show is better read at full size on still ground
   * further down the page.
   */
  product?: React.ReactNode;
  /**
   * A third beat, riding below the window in the same column. Once the window
   * has landed the column travels on and brings this into frame — the way to
   * keep supporting detail inside the shot it belongs to.
   */
  detail?: React.ReactNode;
  id?: string;
  /**
   * Which slice of the photograph this scene travels across. Two scenes on the
   * same plate should take different slices — same photograph, different
   * framings — so the page never shows the identical backdrop twice.
   */
  camera?: CameraFraming;
  /** Length of the camera travel beyond the first viewport. */
  travel?: string;
  /** Height of anything already pinned above the scene (the section nav). */
  offsetTop?: string;
  /**
   * Hide the global floating mobile CTA while this scene is on screen. Opt-in,
   * and only ONE scene per page may claim it: the suppression flag is a single
   * shared boolean, so two scenes toggling it would fight as one leaves and
   * the next arrives. The hero claims it because its statement is seated on
   * the bottom edge; later scenes are not.
   */
  claimsBottomEdge?: boolean;
  className?: string;
}) {
  const sceneRef = useRef<HTMLElement | null>(null);
  const {
    src: plateSrc,
    usingMobile
  } = usePlateSource(photo, photoMobile);
  // Desktop has no fallback at all: a browser that will not decode here is
  // vanishingly rare, and a mismatched still would be the cost paid by
  // everyone to cover it.

  /*
   * THE HORIZONTAL FRAMING BELONGS TO THE CUT, NOT THE SCENE.
   *
   * The desktop plate's launch column sits at 47% of its width, so the camera
   * is deliberately off-centre to put the ascent under the headline. The 9:16
   * cut was authored with the rocket dead centre, so inheriting that same
   * offset pushed it visibly left. On the portrait cut the camera centres.
   */
  const mobileFraming = usingMobile ? {
    fromX: -50,
    toX: -50
  } : null;
  // Held in a ref rather than an effect dependency: the framing is authored
  // once per scene, and re-binding scroll listeners on every render to watch a
  // fresh object literal would be a real cost for a value that never changes.
  const framingRef = useRef({
    ...CAMERA,
    ...camera,
    ...mobileFraming
  });
  framingRef.current = {
    ...CAMERA,
    ...camera,
    ...mobileFraming
  };
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let current = 0;
    let target = 0;
    let started = false;
    let raf = 0;
    let last = 0;
    const measure = () => {
      const rect = scene.getBoundingClientRect();
      const distance = rect.height - window.innerHeight;
      if (distance <= 0) return 0;
      return clamp01(-rect.top / distance);
    };
    const paint = (p: number) => {
      const s = scene.style;
      const cam = framingRef.current;
      const q = clamp01(p / cam.until);
      s.setProperty('--cam-x', `${lerp(cam.fromX, cam.toX, q).toFixed(3)}%`);
      s.setProperty('--cam-y', `${lerp(cam.fromY, cam.toY, q).toFixed(3)}%`);
      s.setProperty('--cam-z', reduce ? '1' : (1 + cam.push * Math.sin(Math.PI * q)).toFixed(4));

      // THE EXIT IS NOT A FADE. Three properties on three curves: the copy is
      // pulled up and taken out of focus FIRST, and only disappears once it is
      // already soft. A plain fade reads as wrong here.
      // Back to the reference proportions: the statement HOLDS for the first
      // third of the travel while the camera alone moves, then exits across a
      // fifth of it. Starting the exit at the first pixel of scroll is what
      // made the scene feel wrong — there was no beat of pure camera before
      // the copy started leaving, so the move never registered as a move.
      const exit = track(p, 0.297, 0.508);
      s.setProperty('--hero-out', Math.pow(exit, 1.75).toFixed(4));
      s.setProperty('--hero-o', Math.pow(1 - clamp01((exit - 0.38) / 0.62), 1.25).toFixed(4));
      // Resolves to `none` at rest so the settled statement is composited with
      // no filter at all and stays pixel-exact.
      s.setProperty('--hero-filter', exit > 0 ? `blur(${(18 * Math.pow(exit, 0.9)).toFixed(2)}px)` : 'none');
      s.setProperty('--hero-vis', exit >= 1 ? 'hidden' : 'visible');

      // The pill/heading and the window arrive at visibly different rates. That
      // difference IS the depth of the arrival — do not unify them.
      // A short fade, then solid. The window is a product screen, not a ghost:
      // a long stretch spent semi-transparent lets the plate read straight
      // through the UI, which undermines the thing it is there to prove.
      // Opacity resolves quickly, but the two travel tracks run almost to the
      // end of the scene on different curves. That is what removes the dead
      // middle: the arriving block is still easing into place long after it is
      // readable, so the scene is never sitting motionless waiting for scroll.
      s.setProperty('--plat-o', track(p, 0.557, 0.623, easeOutCubic).toFixed(4));
      // THE CLAIM MUST COME TO REST BEFORE IT LEAVES.
      // These two tracks used to run to 0.967 / 0.984 while the lift began at
      // 0.90 — so the block was still easing into position when it started
      // travelling back out, and there was never a frame where it simply sat
      // in the middle of the shot being read. They now settle at ~0.80, which
      // opens a genuine HOLD between arrival and departure.
      s.setProperty('--plat-text-p', track(p, 0.563, 0.80, easeOutQuart).toFixed(4));
      s.setProperty('--plat-shot-p', track(p, 0.557, 0.82, easeOutCubic).toFixed(4));
      // The hold runs from ~0.82 to 0.94; the lift is the last 6% of travel,
      // so the beat ends on movement without cutting the reading short.
      s.setProperty('--plat-lift', track(p, 0.94, 1, easeInOutCubic).toFixed(4));
      // Keeps the off-stage frame out of the a11y tree and out of hit-testing.
      s.setProperty('--plat-vis', p > 0.54 ? 'visible' : 'hidden');

      // THE PLATE IS SCRUBBED, NOT PLAYED. Scroll position drives the video's
      // playhead, so the clip's own motion (launch -> light warp) advances at
      // exactly the reader's pace and reverses when they scroll back. It is
      // the same idea as the pan: one continuous move the reader controls.
      progressRef.current = p;
      const video = plateRef.current;
      // Never queue a seek on top of an in-flight one: on a sparsely
      // keyframed clip that backs up and the picture visibly stutters behind
      // the pan. Skipping the frame instead keeps the two locked together.
      if (video && video.duration && !video.seeking) {
        // A hair short of the end: seeking to exactly duration parks some
        // decoders on a blank frame.
        const t = p * (video.duration - 0.04);
        if (Math.abs(video.currentTime - t) > 0.02) {
          // fastSeek lands on the nearest decodable frame rather than doing a
          // precise seek — far cheaper, and imperceptible while scrubbing.
          if (typeof video.fastSeek === 'function') video.fastSeek(t);else video.currentTime = t;
        }
      }

      // will-change is scoped to actual movement: held on at rest it softens
      // text and image rasterisation.
      if (p > 0.001 && p < 0.999) scene.setAttribute('data-moving', '');else scene.removeAttribute('data-moving');
    };
    const frame = (now: number) => {
      const dt = last ? Math.min(0.05, (now - last) / 1000) : 0.016;
      last = now;
      if (reduce) {
        current = target;
      } else {
        let step = (target - current) * (1 - Math.exp(-SETTLE * dt));
        const cap = MAX_RATE * dt;
        if (step > cap) step = cap;else if (step < -cap) step = -cap;
        current += step;
        if (Math.abs(target - current) < EPSILON) current = target;
      }
      paint(current);
      if (current !== target) {
        raf = requestAnimationFrame(frame);
      } else {
        raf = 0;
        last = 0;
      }
    };
    const sync = () => {
      target = measure();
      if (!started) {
        started = true;
        current = target;
        paint(current);
        return;
      }
      if (!raf && current !== target) {
        last = 0;
        raf = requestAnimationFrame(frame);
      }
    };
    /**
     * A re-measure is NOT a scroll gesture, so it must never be smoothed. The
     * scene's height is expressed in svh and the plate's width in vw, so
     * anything that changes the viewport — the page settling on load, the
     * scrollbar appearing, an embed sizing itself, a device rotation — moves
     * the target without the reader having moved. Feeding that through the
     * smoothing loop played the camera move on its own, which read as the
     * image slowly zooming in the moment the page opened.
     */
    const snap = () => {
      if (raf) {
        cancelAnimationFrame(raf);
        raf = 0;
        last = 0;
      }
      started = true;
      target = measure();
      current = target;
      paint(current);
    };
    snap();
    // The plate is a large decode; when it lands the layout is final, so
    // re-register the timeline against it rather than animating into place.
    const image = scene.querySelector('img');
    if (image && !image.complete) image.addEventListener('load', snap, {
      once: true
    });
    // Same for a motion plate: once its metadata is in, the duration is known
    // and the first scrubbed frame can be placed against the real timeline.
    const plate = scene.querySelector('video');
    if (plate) plate.addEventListener('loadedmetadata', snap);
    window.addEventListener('scroll', sync, {
      passive: true
    });
    window.addEventListener('resize', snap);
    return () => {
      window.removeEventListener('scroll', sync);
      window.removeEventListener('resize', snap);
      if (image) image.removeEventListener('load', snap);
      if (plate) plate.removeEventListener('loadedmetadata', snap);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  // One fluid unit for every travel distance in the scene, fitted to whichever
  // of width or height is tighter so the move keeps its shape at any aspect
  // ratio. Every distance below is written as calc(N * var(--u)).
  const isMotionPlate = /\.(mp4|webm|mov)(\?|$)/i.test(plateSrc);
  const plateRef = React.useRef<HTMLVideoElement | null>(null);
  const progressRef = React.useRef(0);

  /*
   * WHY THE PLATE WAS BLANK ON PHONES.
   *
   * This clip is never played — the scroll owns the playhead, and the code
   * only ever seeks. On desktop that is fine. iOS Safari will NOT decode or
   * paint a <video> that has never entered a playing state: `preload="auto"`
   * is silently downgraded to metadata on cellular and Low Power Mode, and a
   * seek on an undecoded element paints nothing. The hero therefore rendered
   * as the flat void colour on exactly the devices most likely to see it.
   *
   * The fix is to PRIME it: play for one frame (permitted, since it is muted
   * and playsInline), pause immediately, then seek to the current scroll
   * position. Nothing is visible playing — it is a decode kick.
   *
   * When priming is refused outright — Low Power Mode blocks even muted
   * autoplay — there is no way to get a frame out of the element at all, so
   * the scene falls back to the still the clip was generated from. It takes
   * the identical camera transform, so the section still works; it simply
   * does not move.
   */
  React.useEffect(() => {
    if (!isMotionPlate) return;
    const el = plateRef.current;
    if (!el) return;
    let cancelled = false;
    const seekToScroll = () => {
      if (!el.duration) return;
      try {
        el.currentTime = progressRef.current * (el.duration - 0.04);
      } catch {
        /* duration not settled yet — the scroll handler will catch up */
      }
    };
    const prime = () => {
      const attempt = el.play();
      if (attempt && typeof attempt.then === 'function') {
        attempt.then(() => {
          if (cancelled) return;
          el.pause();
          seekToScroll();
        }).catch(() => {
          // Autoplay refused (Low Power Mode). Seek anyway — once metadata is
          // in, a seek is still the best chance of getting a painted frame,
          // and the element MUST stay mounted either way: removing it
          // guarantees the void, whereas leaving it may still resolve.
          if (!cancelled) seekToScroll();
        });
      } else {
        el.pause();
        seekToScroll();
      }
    };
    // load() matters on iOS: without it the element can sit at readyState 0
    // indefinitely when the browser has decided to defer the fetch.
    el.load();
    if (el.readyState >= 1) prime();else el.addEventListener('loadedmetadata', prime, {
      once: true
    });
    // A slow connection is not a failure. The element stays mounted and keeps
    // trying; the retry re-primes if metadata has since arrived.
    const guard = window.setTimeout(() => {
      if (!cancelled && el.readyState >= 1) prime();
    }, 2500);
    return () => {
      cancelled = true;
      window.clearTimeout(guard);
      el.removeEventListener('loadedmetadata', prime);
      window.clearTimeout(guard);
    };
  }, [isMotionPlate, plateSrc]);
  /*
   * THE HERO OWNS THE BOTTOM OF THE SCREEN WHILE IT IS ON SCREEN.
   *
   * Layout pins a floating "Get started" to the bottom edge on phones as soon
   * as the page has moved 20px. That rule assumes a normal scrolling hero you
   * pass in a few hundred pixels — but this scene is pinned for 220svh, so the
   * button appeared over the headline almost immediately and stayed there for
   * the whole opening statement. The scene claims the bottom edge for as long
   * as it is in view, and releases it the moment it leaves.
   */
  const [sceneInView, setSceneInView] = React.useState(true);
  useSuppressFloatingCta(claimsBottomEdge && sceneInView);
  useEffect(() => {
    if (!claimsBottomEdge) return;
    const scene = sceneRef.current;
    if (!scene) return;
    const observer = new IntersectionObserver(([entry]) => setSceneInView(entry.isIntersecting), {
      threshold: 0
    });
    observer.observe(scene);
    return () => observer.disconnect();
  }, [claimsBottomEdge]);
  const sceneStyle = {
    height: `calc(100svh + ${travel})`,
    '--u': 'clamp(0.6px, min(calc(100vw / 1440), calc(100svh / 940)), 1.15px)'
  } as React.CSSProperties;
  return <section ref={sceneRef} id={id} data-camera-scene="" className={`relative ${className}`} style={sceneStyle} data-id="element-540" data-mp="1a6vbv57u7dfg">
      {/* The plate plays at full strength — no veil, no grade, no wash. The
       ground underneath is the site's ink (#1A120C) and nothing else: it
       exists only so the cream page never flashes through before the plate
       decodes. It was a navy (#080A19), visible as a distinctly blue screen
       on first load — a colour that appears nowhere else on this site. */}
      {/* The statement seats in the LOWER THIRD, not the centre: the plate's
       subject is a vertical ascent, and copy across the middle cuts the rocket
       in half. Sitting it low leaves the climb whole above the words, and the
       headline reads as the ground the launch rises out of. */}
      {/* The statement used to reserve an extra 80px below md as clearance for
       the global floating CTA, which pinned itself to the bottom of the
       viewport the moment the page moved at all — pushing the headline down
       the screen for the entire scene AND still colliding with it, because the
       hero is one long pinned scene rather than a section you scroll past.
       The scene now suppresses that button outright while it is on screen, so
       the seating is the same 7svh at every width. */}
      <div className="sticky isolate flex w-full flex-col items-center justify-end overflow-hidden bg-[#1A120C] pb-[14svh] md:pb-[9svh]" style={{
      top: offsetTop,
      height: `calc(100svh - ${offsetTop})`
    }} data-id="element-541" data-mp="2qjkgjsdw8pv4">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10" data-id="element-542" data-mp="30bzzarx0lih2">
          <div className="camera-lens absolute inset-0" style={{
          transform: 'scale(var(--cam-z, 1))',
          transformOrigin: '50% 50%'
        }} data-id="element-543" data-mp="35atcskcax9h8">
            {/* The camera move is identical whether the plate is a still or a
             motion loop — same sizing, same transform. A video only adds its
             own internal movement underneath the pan. */}
            {/* No poster. A still that is not this clip's own first frame
             would cut to a different composition the moment the video
             decodes; the prime above paints the real first frame instead. */}
            {isMotionPlate ? <video key={plateSrc} ref={plateRef} src={plateSrc} muted playsInline autoPlay preload="auto" aria-hidden="true" onLoadedMetadata={e => {
            // Never plays on its own — the playhead belongs to the scroll.
            const el = e.currentTarget;
            el.pause();
            if (el.duration) el.currentTime = progressRef.current * (el.duration - 0.04);
          }} className="camera-photo absolute left-1/2 top-1/2 max-w-none object-cover" style={{
            width: 'max(112%, 132svh)',
            aspectRatio: '9 / 16',
            transform: 'translate(var(--cam-x, -47%), var(--cam-y, -79%))'
          }} data-id="element-544" data-mp="1lcjjbvs7z2b5" /> : <img src={plateSrc} alt="" aria-hidden="true" className="camera-photo absolute left-1/2 top-1/2 h-auto max-w-none" style={{
            // Preserves the crop at any aspect ratio: the photograph is always
            // at least as wide as the viewport and always tall enough to pan.
            // Sized past the viewport so the visible band is a fifth of the
            // plate rather than a quarter — the 5K source carries the detail,
            // and the tighter band keeps each beat on one region of the image.
            // A 9:16 plate is already far taller than the viewport at close to
            // 1:1 width, so it needs almost no magnification to have room to
            // pan — which is what keeps it sharp. Landscape sources had to be
            // blown up ~2.8x viewport height and went soft as a result.
            width: 'max(112%, 132svh)',
            aspectRatio: isMotionPlate ? '9 / 16' : undefined,
            transform: 'translate(var(--cam-x, -47%), var(--cam-y, -79%))'
          }} data-id="element-545" data-mp="1ju1dqlnl01nc" />}
          </div>
        </div>

        {/* The LIFT goes on the wrapper so the blocks hold their spacing. The
         opacity and blur go on each block individually — through an ancestor
         filter, engines disagree about whether a descendant with its own
         compositing feature is drawn through the filter or beside it. */}
        <div className="camera-statement relative w-full" style={{
        visibility: 'var(--hero-vis, visible)',
        transform: 'translateY(calc(var(--hero-out, 0) * -163 * var(--u)))'
      }} data-id="element-546" data-mp="2hntyi2pcv4nn">
          {statement}
        </div>

        {/* With a product window the column starts near the top and has room to
         travel; with copy alone there is nothing below it, so pinning it high
         just leaves a screen of empty plate underneath. Centre it instead. */}
        <div className={`camera-product absolute left-1/2 w-full max-w-[1060px] -translate-x-1/2 px-5 md:px-10 ${product || detail ? 'top-[4%] md:top-[6%]' : 'top-1/2 -translate-y-1/2'}`} style={{
        opacity: 'var(--plat-o, 1)',
        visibility: 'var(--plat-vis, hidden)'
      }} data-id="element-547" data-mp="6xvig3k8wk0u">
          {/* One tall column, lifted as a whole once it has landed. The screen
           and the points that explain it are never on different grounds. */}
          {/* Once it has landed the column travels on rather than sitting
           still until the scene ends. With a window below it that brings the
           supporting detail into frame; with copy alone it carries the claim
           up across the disc, so the beat ends on movement instead of a
           held frame waiting to be scrolled past. */}
          {/* THE END LIFT IS A DESKTOP GESTURE ON A COPY-ONLY SCENE.
           With a window below it the lift is load-bearing — it is what brings
           the supporting detail into frame. With copy alone it exists only so
           the beat ends on movement, and on a phone that movement carried the
           claim up off the vertical centre and towards the top edge before the
           scene released. Below md the multiplier is zero for copy-only
           scenes: the sentence holds dead centre and the reader simply scrolls
           on to the next section. */}
          <div className={`flex w-full flex-col items-center ${product || detail ? '[--lift-k:1]' : '[--lift-k:0] md:[--lift-k:1]'}`} style={{
          transform: `translateY(calc(var(--plat-lift, 0) * var(--lift-k, 1) * ${product || detail ? -430 : -300} * var(--u)))`
        }} data-id="element-548" data-mp="2r3ud6e22k2yn">
            <div className="flex w-full flex-col items-center text-center" style={{
            transform: 'translateY(calc((1 - var(--plat-text-p, 1)) * 638 * var(--u)))'
          }} data-id="element-549" data-mp="2dcpjdsldu4w8">
              {productHeader}
            </div>
            {(product || detail) && <div className="mt-7 w-full md:mt-10" style={{
            transform: 'translateY(calc((1 - var(--plat-shot-p, 1)) * 555 * var(--u)))'
          }} data-id="element-550" data-mp="1ygrp6fv0ramn">
                {/* Below the desktop composition the window stays ONE
                 proportional unit — scaled, never reflowed. Re-laying out a
                 product screen to fit a phone turns the proof into a different
                 product. */}
                {/* Fluid rather than stepped: a fixed 0.8 floor still overran
                 the narrowest phones, because the window's intrinsic width
                 does not shrink with the viewport. Scaling on vw keeps it
                 inside the frame all the way down to 320px. */}
                {product && <div className="origin-top scale-[min(1,calc(100vw/1180))] md:scale-100" data-id="element-551" data-mp="34deayxypmdfm">
                    {product}
                  </div>}
                {detail && <div className="mt-16 md:mt-24" data-id="element-552" data-mp="2umjhb8j4wbk5">{detail}</div>}
              </div>}
          </div>
        </div>
      </div>
    </section>;
}

/**
 * One block of the opening statement. Carries the exit's opacity and blur so no
 * ancestor filter is involved, and leaves the intro animation free to live on
 * whatever it wraps.
 */
export function SceneBlock({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={className} style={{
    opacity: 'var(--hero-o, 1)',
    filter: 'var(--hero-filter, none)'
  }} data-id="element-553" data-mp="1x2a0qlsd043p">
      {children}
    </div>;
}